import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutFooter } from '@/scopes/checkout/components/CheckoutFooter'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import {
  Content,
  Loader,
  MainContent,
  PageLayout,
  PrimaryContent,
  SideBarContent,
} from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { Basket } from '../components/Basket'
import CheckoutConfirmationHero from '../components/CheckoutConfirmationHero/CheckoutConfirmationHero'
import CheckoutWhatNext from '../components/CheckoutWhatNext/CheckoutWhatNext'
import { EngineError } from '../components/EngineError/EngineError'
import { Faq } from '../components/Faq/Faq'
import { TrustIcons } from '../components/TrustIcons/TrustIcons'
import { shouldShowTrustIcons } from '../domain/policies/brand'
import Travelers from '../components/Travelers/Travelers'
import Accommodation from '../components/Accomodation/Accommodation'
import Extras from '../components/Extras/Extras'
import Transfer from '../components/Transfer/Transfer'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const brandName = useConfiguration('brandName')
  const showTrustIcon = shouldShowTrustIcons(brandName)
  const intl = useIntl()

  useSendCheckoutEvent('checkout_4_reached')

  return (
    <PageLayout>
      {data?.error && data.error.hasError
        ? (
            !data.error.errorCode && data.error.redirectUrl
              ? (
                  <CheckoutError />
                )
              : (
                  <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
                    <CheckoutHeader />
                    <main id="main-content" tabIndex={-1}>
                      <EngineError error={data.error} />
                    </main>
                  </NewRelicErrorBoundary>
                )
          )
        : (
            <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
              <CheckoutHeader />
              <MainContent>
                {isLoading
                  ? (
                      <Loader text={intl.formatMessage({ id: 'checkout_steps_loading_confirmation_step' })} />
                    )
                  : (
                      <>
                        <CheckoutConfirmationHero />
                        <Content>
                          <PrimaryContent>
                            <CheckoutWhatNext />
                            <Accommodation />
                            <Travelers/>
                            <Transfer />
                            <Extras />
                          </PrimaryContent>
                          <SideBarContent>
                            <Basket />
                            <div>
                              <Faq />
                              {showTrustIcon && <TrustIcons />}
                            </div>
                          </SideBarContent>
                        </Content>
                      </>
                    )}
              </MainContent>
              {!isLoading && <CheckoutFooter />}
            </NewRelicErrorBoundary>
          )}
    </PageLayout>
  )
}

export default function CheckoutConfirmationPage() {
  const contextItemId = useCMSPageField('contextItemId')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    step: 4,
  }), [contextItemId])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <MetaTitle titleTranslationTag="checkout_steps_step_confirmation_meta_title" />
          <Page />
          <DevTools />
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
